<script setup lang="ts">
import BaseDialog from './BaseDialog.vue';
import BaseLink from './BaseLink.vue';
import { computed, ref } from 'vue';
import { constant } from '@/constant';
import LiteYouTubeEmbed from 'vue-lite-youtube-embed';
import 'vue-lite-youtube-embed/style.css';
import mime from 'mime-types';
import { getContextIconName } from '@/utils/context-icon';
import { getLocale } from '@/utils/data-display-utils';
import { formatFileSize } from '@/utils/file-util';

// REFS
const mediaDialog = ref<InstanceType<typeof BaseDialog>>();

// PROPS
const props = defineProps<{
  type: number;
  link?: string;
  title: string;
  content?: any;
  image?: string;
  truncateTitle?: boolean;
  descriptiveTextSize?: 'default' | 'sm';
}>();

// EMITS
const emit = defineEmits<{
  (e: 'openAttachmentDialog'): void;
}>();

// FUNCTIONS
function openDialog() {
  emit('openAttachmentDialog');
  mediaDialog.value?.openDialog();
}

function closeDialog() {
  mediaDialog.value?.closeDialog();
}

defineExpose({
  openDialog,
  closeDialog,
});
const slide = ref(1);

const open = (url: string) => {
  window.open(url, '_blank');
};

const contextIconName = computed(() => getContextIconName(props.type, 'outlined'));
</script>

<template>
  <button
    class="base-media"
    :class="{ 'base-media--truncate': props.truncateTitle }"
    @click="openDialog()"
    :aria-haspopup="props.type != constant.Context.Type.Link ? 'dialog' : undefined"
  >
    <div class="base-media__type">
      <q-icon class="icon" :name="contextIconName" />
    </div>
    <div
      v-if="props.type != constant.Context.Type.Link"
      class="base-media__descriptive-text"
      :class="{
        'base-media__descriptive-text--truncate': props.truncateTitle,
        'base-media__descriptive-text--sm': props.descriptiveTextSize === 'sm',
      }"
    >
      <span class="base-media__title-text" :class="{ 'base-media__title-text--truncate': props.truncateTitle }">{{
        props.title
      }}</span>
      <BaseDialog ref="mediaDialog">
        <template v-slot:title>
          <span class="title">{{ props.title }}</span>
        </template>
        <template v-slot:content>
          <div v-if="props.type === constant.Context.Type.Text" class="ck-rich-text" v-html="props.content"></div>
          <template v-if="props.type === constant.Context.Type.Image">
            <div class="q-pa-md full-height">
              <q-carousel
                v-model="slide"
                swipeable
                animated
                control-color="primary"
                navigation
                infinite
                padding
                arrows
                class="rounded-borders"
              >
                <q-carousel-slide v-for="(image, index) in props.content" :name="index + 1">
                  <img :src="image.url" height="100%" width="100%" />
                  <div class="image-details">
                    <div class="image-name">
                      <i>{{ image.name }}</i>
                    </div>
                    <q-btn flat class="fullscreen-btn" @click="open(`${image.url}`)">
                      <q-icon name="open_in_new"></q-icon>
                    </q-btn>
                  </div>
                </q-carousel-slide>
              </q-carousel>
            </div>
          </template>
          <LiteYouTubeEmbed
            v-if="props.type === constant.Context.Type.Video"
            :id="props.content"
            :title="props.title"
          />
          <template v-if="props.type === constant.Context.Type.File">
            <div v-for="file in props.content" class="q-mb-sm">
              <q-btn
                style="pointer-events: auto"
                class="row rounded-borders content-file-download q-pa-sm"
                :href="file.url"
                target="_blank"
              >
                <div class="file-download-type">
                  {{ mime.extension(file.mimetype) }}
                </div>
                <div class="column col-8">
                  <div class="row no-wrap">
                    <span class="text-bold">{{ file.name }} &nbsp;</span>
                    <span class="text-italic">({{ formatFileSize(file.size, getLocale([])) }})</span>
                  </div>
                </div>
                <q-icon name="download" class="col-2 text-h6" />
              </q-btn>
            </div>
          </template>
        </template>
      </BaseDialog>
    </div>
    <div v-else>
      <BaseLink :text="props.title" :destination="props.content" />
    </div>
  </button>
</template>

<style lang="scss">
.base-media {
  display: flex;
  flex-direction: row;
  border-radius: 8px;
  font-size: $sub-header;
  padding: 2px $space-md;
  cursor: pointer;
  font-weight: 500;
  background-color: transparent;
  border: none;
  color: $color-neutral-black-text;
  text-align: left;

  &:hover {
    color: $color-neutral-grey-400-50;
  }

  &__type {
    padding: 0 10px 0 0;

    .icon {
      font-size: $small-title;
    }
  }

  &__descriptive-text {
    width: auto;
    font-size: $sub-header;

    &--sm {
      font-size: 14px;
      line-height: 1.35;
    }

    &--truncate {
      flex: 1 1 auto;
      min-width: 0;
      text-align: left;
    }
  }

  &__title-text {
    &--truncate {
      display: block;
      width: 100%;
      min-width: 0;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      text-align: left;
    }
  }

  .base-link {
    font-size: $small-text;
  }

  &--truncate {
    align-items: center;
    gap: 8px;

    .base-media__type {
      padding: 0;
      width: 20px;
      min-width: 20px;
      display: inline-flex;
      justify-content: center;
    }
  }
}

.title {
  font-weight: bold;
}

.content-file-download {
  display: flex;
  padding: 16px;
  align-items: center;

  .file-download-type {
    border-radius: 8px;
    color: #00aeef;
    padding: 16px;
    align-items: center;
    justify-content: center;
    display: flex;
    margin-right: 16px;
    letter-spacing: 0.05em;
    width: 48px;
    height: 48px;
    border: 2px solid;
  }
}

.q-carousel__slide {
  .image-details {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    margin-bottom: 48px;

    .image-name {
      font-size: $small-text;
      font-style: italic;
    }
  }
}
</style>
